import { Button } from 'primereact/button'
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react'

import useInterfaceLocale from '../../modules/locale/useInterfaceLocale'
import NavigationPanelTime from './NavigationPanelTime'
import RepeatVideos from '../RepeatVideos/RepeatVideos'
import SideTextButton from '../SideText/SideTextButton'
import RedirectMessage from '../RedirectMessage/RedirectMessage'
import NavigationPanelPoweredBy from './NavigationPanelPoweredBy'
import NavigationPanelPrivacy from './NavigationPanelPrivacy'
import NavigationPanelScreenNumber from './NavigationPanelScreenNumber'
import HooksModule from '../../modules/hooks/Module'
import ClientModule from '../../modules/client/Module'
import { SURVEY_EXIT_CONFIRM_EVENT } from '../../modules/hooks/events'

import {
  DEFAULT_NAVIGATION_OPTIONS,
  setNavigationOptionsDisableNavigation,
  setNavigationOptionsDisableNext,
  setNavigationOptionsDisablePrev,
  setNavigationOptionsEnableNavigation,
  setNavigationOptionsEnableNext,
  setNavigationOptionsEnablePrev,
  setNavigationOptionsHidePrev,
  setNavigationOptionsPostNext,
  setNavigationOptionsPostPrev,
  setNavigationOptionsPreNext,
  setNavigationOptionsPrePrev,
  setNavigationOptionsShowPrev
} from './navigationOptionsUpdates'
import { layoutIsCompact } from '../../lib/layout'

import { JUMP_TO_START_OVER_ID } from '../../modules/preview/components/const'

import useViewport from '../../modules/viewport/useViewport'
import useSession from '../useSession'
import useTheme from '../../modules/theme/useTheme'

import { ActionType, IAction, SurveyMode } from '../../actions/types'
import { INavigationOptions, ISessionNavigation } from '../types'
import { QuestionNavigationFrame } from '../../questions/types'

import style from './style.module.scss'
import { hapticEffects } from '../../lib/hapticEffects'
export interface INavigationPanelProps {
  action: IAction
  onPrevRef: React.MutableRefObject<() => void>
  onNextRef: React.MutableRefObject<() => void>
  onLoadingRef: React.MutableRefObject<(loading: boolean) => void>
  progressTotal: number
  progressCurrent: number
  progressQuestionTime: number
  enableRestart: boolean
  showRepeatVideos: boolean
  showSideText: boolean
  screenNumber: number
  navigationFrame: QuestionNavigationFrame
}

const NavigationPanel = React.memo(
  React.forwardRef<ISessionNavigation, INavigationPanelProps>(
    (
      {
        action,
        onPrevRef,
        onNextRef,
        onLoadingRef,
        progressTotal,
        progressCurrent,
        progressQuestionTime,
        enableRestart,
        showRepeatVideos,
        showSideText,
        screenNumber,
        navigationFrame
      },
      ref
    ) => {
      const [navigationOptions, setNavigationOptions] = useState<INavigationOptions>(DEFAULT_NAVIGATION_OPTIONS)
      const navigationOptionsRef = useRef<INavigationOptions>(navigationOptions)
      navigationOptionsRef.current = navigationOptions

      const [exiting, setExiting] = useState(false)
      const exitRequestedRef = useRef(false)

      const { t, localeOptions } = useInterfaceLocale()
      const viewport = useViewport()
      const session = useSession()
      const theme = useTheme()
      const imperativeHandleRef = useRef<() => ISessionNavigation>(null)

      // PV exit hand-back: the exit POST fires only after the PV dialog confirms (single-fire; error re-enables)
      useEffect(() => {
        if (!HooksModule.hasExitHook) return
        const onExitConfirm = async () => {
          if (exitRequestedRef.current) return
          exitRequestedRef.current = true
          setExiting(true)
          const nextAction = await ClientModule.requestExitAction(session.action)
          if (nextAction && nextAction.action !== ActionType.ERROR) {
            session.doAction(nextAction)
          } else {
            exitRequestedRef.current = false
            setExiting(false)
          }
        }
        window.addEventListener(SURVEY_EXIT_CONFIRM_EVENT, onExitConfirm)
        return () => window.removeEventListener(SURVEY_EXIT_CONFIRM_EVENT, onExitConfirm)
      }, [session])

      if (null === imperativeHandleRef.current) {
        // !!! CAREFUL WITH EDITING THIS, CURRENT STATE HAS NO DYNAMIC DEPENDENCIES, SO IT DOESN'T NEED TO BE RECREATED
        imperativeHandleRef.current = () => {
          return {
            enable(enable: boolean) {
              const nextNavigationOptions = enable
                ? setNavigationOptionsEnableNavigation(navigationOptionsRef.current)
                : setNavigationOptionsDisableNavigation(navigationOptionsRef.current)
              navigationOptionsRef.current = nextNavigationOptions
              setNavigationOptions(nextNavigationOptions)
            },
            get enabled() {
              return navigationOptionsRef.current.enabled
            },
            get loading() {
              return navigationOptionsRef.current.next.loading || navigationOptionsRef.current.prev.loading
            },
            enableNext(enable) {
              const nextNavigationOptions = enable
                ? setNavigationOptionsEnableNext(navigationOptionsRef.current)
                : setNavigationOptionsDisableNext(navigationOptionsRef.current)
              navigationOptionsRef.current = nextNavigationOptions
              setNavigationOptions(nextNavigationOptions)
            },
            get nextEnabled() {
              return navigationOptionsRef.current.next.present && navigationOptionsRef.current.next.enabled
            },
            showPrev(show) {
              const nextNavigationOptions = show
                ? setNavigationOptionsShowPrev(navigationOptionsRef.current)
                : setNavigationOptionsHidePrev(navigationOptionsRef.current)
              navigationOptionsRef.current = nextNavigationOptions
              setNavigationOptions(nextNavigationOptions)
            },
            get prevPresent() {
              return navigationOptionsRef.current.prev.present
            },
            enablePrev(enable) {
              const nextNavigationOptions = enable
                ? setNavigationOptionsEnablePrev(navigationOptionsRef.current)
                : setNavigationOptionsDisablePrev(navigationOptionsRef.current)
              navigationOptionsRef.current = nextNavigationOptions
              setNavigationOptions(nextNavigationOptions)
            },
            get prevEnabled() {
              return navigationOptionsRef.current.prev.present && navigationOptionsRef.current.prev.enabled
            },
            preNext() {
              const nextNavigationOptions = setNavigationOptionsPreNext(navigationOptionsRef.current)
              navigationOptionsRef.current = nextNavigationOptions
              setNavigationOptions(nextNavigationOptions)
            },
            postNext() {
              const nextNavigationOptions = setNavigationOptionsPostNext(navigationOptionsRef.current)
              navigationOptionsRef.current = nextNavigationOptions
              setNavigationOptions(nextNavigationOptions)
            },
            prePrev() {
              const nextNavigationOptions = setNavigationOptionsPrePrev(navigationOptionsRef.current)
              navigationOptionsRef.current = nextNavigationOptions
              setNavigationOptions(nextNavigationOptions)
            },
            postPrev() {
              const nextNavigationOptions = setNavigationOptionsPostPrev(navigationOptionsRef.current)
              navigationOptionsRef.current = nextNavigationOptions
              setNavigationOptions(nextNavigationOptions)
            }
          }
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
      useImperativeHandle<ISessionNavigation, ISessionNavigation>(ref, imperativeHandleRef.current, [])

      const lastLoadingRef = useRef<boolean>(null)
      useEffect(() => {
        const nextLoading = navigationOptions.next.loading || navigationOptions.prev.loading
        if (nextLoading !== lastLoadingRef.current) {
          lastLoadingRef.current = nextLoading
          if (onLoadingRef.current) {
            onLoadingRef.current(nextLoading)
          }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [navigationOptions])

      if (!navigationOptions.enabled) {
        return enableRestart ? (
          <div
            className={`${style.NavigationPanel}${localeOptions.rtl ? ' ' + style.NavigationPanelRTL : ''} ${
              viewport.mobile ? style.mobile : ''
            } ${layoutIsCompact(session.props.layout) ? style.compact : ''} PSWidgetC`}
            data-testid="SW-NavigationPanel"
          >
            <div className={style.NavigationPanelPrevButton}>
              <Button
                label={t('preview', 'texts', 'restart')}
                aria-label={t('preview', 'texts', 'restart')}
                onClick={() =>
                  void session.jumpToQuestion(JUMP_TO_START_OVER_ID).catch(() => {
                    // errors are handled in the session
                  })
                }
                className="p-button-secondary"
                icon="icon-aytmfd-restart-survey"
                iconPos={localeOptions.rtl ? 'right' : 'left'}
                data-testid="SW-RestartButton"
              />
            </div>
            <RedirectMessage action={action} />
          </div>
        ) : null
      } else {
        const prevButton = navigationOptions.prev.present ? (
          <div className={style.NavigationPanelPrevButton}>
            <Button
              label=""
              aria-label={t('preview', 'texts', 'prev')}
              disabled={!navigationOptions.prev.enabled}
              onClick={onPrevRef.current}
              loading={navigationOptions.prev.loading}
              className="p-button-secondary"
              icon={localeOptions.rtl ? 'pi pi-chevron-right' : 'pi pi-chevron-left'}
              iconPos={localeOptions.rtl ? 'right' : 'left'}
              data-testid="SW-PrevButton"
            />
          </div>
        ) : null

        const onClickNext = () => {
          onNextRef.current()
          if (navigationOptions.next.enabled) {
            hapticEffects.success()
          }
        }
        const nextButton = navigationOptions.next.present ? (
          <div className={style.NavigationPanelNextButton}>
            <Button
              label={t('preview', 'texts', 'next')}
              aria-label={t('preview', 'texts', 'next')}
              aria-disabled={!navigationOptions.next.enabled}
              onClick={onClickNext}
              loading={navigationOptions.next.loading}
              className={`p-button-primary${navigationOptions.next.enabled ? '' : ' p-disabled'}`}
              icon={localeOptions.rtl ? 'pi pi-chevron-left' : 'pi pi-chevron-right'}
              iconPos={localeOptions.rtl ? 'left' : 'right'}
              data-testid="SW-NextButton"
            />
          </div>
        ) : null

        const content = (
          <div className={style.NavigationPanelContent}>
            {HooksModule.hasExitHook ? (
              <i
                className={`${style.exitLink}${exiting ? ` ${style.exitLinkDisabled}` : ''} icon-aytmfd-exit`}
                onClick={() => {
                  if (exiting) return
                  // hand exit intent to the PV dialog; the exit flow starts on its confirm event
                  HooksModule.doExit(Boolean(session.features?.feedback))
                }}
                title={t('preview', 'texts', 'exit')}
              />
            ) : null}
            {theme.style.tp ? (
              <NavigationPanelTime
                mode={action?.mode}
                progressTotal={progressTotal}
                progressCurrent={progressCurrent}
                progressQuestionTime={progressQuestionTime}
                key="NavigationPanelTime"
              />
            ) : null}
            {showRepeatVideos ? <RepeatVideos key="RepeatVideos" /> : null}
            {showSideText ? <SideTextButton action={action} key="SideTextButton" /> : null}
            <NavigationPanelPrivacy mode={action?.mode} key="NavigationPanelPrivacy" />
            <NavigationPanelPoweredBy mode={action?.mode} domain={action?.aytm_domain} key="NavigationPanelPoweredBy" />

            {screenNumber && session.surveyMode == SurveyMode.RESPONDENT ? (
              <NavigationPanelScreenNumber
                screenNumber={screenNumber}
                navigationFrame={navigationFrame}
                key="NavigationPanelScreenNumber"
              />
            ) : null}
          </div>
        )

        const oberig = (
          <div
            className={style.NavigationPanelOberig}
            aria-hidden={true}
            dangerouslySetInnerHTML={{ __html: t('preview', 'texts', 'oberig') }}
          />
        )

        return (
          <div
            className={`${style.NavigationPanel}${localeOptions.rtl ? ' ' + style.NavigationPanelRTL : ''} ${
              viewport.mobile ? style.mobile : ''
            } ${theme.style.noTransparency ? style.noTransparency : ''}  ${
              layoutIsCompact(session.props.layout) ? style.compact : ''
            } PSWidgetC`}
            role="navigation"
            data-testid="SW-NavigationPanel"
          >
            {prevButton}
            {content}
            {!session.features.hideOberig ? oberig : null}
            {nextButton}
          </div>
        )
      }
    }
  ),
  (prevProps, nextProps) =>
    prevProps.action === nextProps.action &&
    prevProps.progressTotal === nextProps.progressTotal &&
    prevProps.progressCurrent === nextProps.progressCurrent &&
    prevProps.progressQuestionTime === nextProps.progressQuestionTime &&
    prevProps.enableRestart === nextProps.enableRestart &&
    prevProps.showRepeatVideos === nextProps.showRepeatVideos &&
    prevProps.showSideText === nextProps.showSideText &&
    prevProps.screenNumber === nextProps.screenNumber &&
    prevProps.navigationFrame === nextProps.navigationFrame
)

NavigationPanel.displayName = 'NavigationPanel'

export default NavigationPanel
